import { createContext, useCallback, useContext, useEffect, useRef, useState, type ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { api, errorText, setUnauthorizedHandler } from './api';
import { setReportingTz } from './format';
import type { SettingsResponse, User } from './types';

interface Session {
  user: User | null | undefined; // undefined while the first /me is in flight
  settings: SettingsResponse | null;
  settingsError: string;
  setUser: (u: User | null) => void;
  reloadSettings: () => Promise<void>;
  signOut: () => Promise<void>;
}

const Ctx = createContext<Session | null>(null);

export function SessionProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null | undefined>(undefined);
  const [settings, setSettings] = useState<SettingsResponse | null>(null);
  const [settingsError, setSettingsError] = useState('');
  const navigate = useNavigate();
  const location = useLocation();
  const locRef = useRef(location);
  locRef.current = location;

  useEffect(() => {
    setUnauthorizedHandler(() => {
      setUser(null);
      const here = locRef.current.pathname + locRef.current.search;
      if (!here.startsWith('/login')) navigate(`/login?next=${encodeURIComponent(here)}`, { replace: true });
    });
    // Any failure (401, or the API unreachable) leads to the login form, which shows the
    // server's message when the user tries to sign in.
    api<User>('/api/auth/me', { noRedirect: true })
      .then(setUser)
      .catch(() => setUser(null));
    return () => setUnauthorizedHandler(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const reloadSettings = useCallback(async () => {
    try {
      const s = await api<SettingsResponse>('/api/settings');
      setReportingTz(s.values.reporting_timezone);
      setSettings(s);
      setSettingsError('');
    } catch (e) {
      setSettingsError(errorText(e));
    }
  }, []);

  useEffect(() => {
    if (user) void reloadSettings();
  }, [user, reloadSettings]);

  const signOut = useCallback(async () => {
    try {
      await api('/api/auth/logout', { method: 'POST' });
    } finally {
      setUser(null);
      setSettings(null);
      navigate('/login', { replace: true });
    }
  }, [navigate]);

  return <Ctx.Provider value={{ user, settings, settingsError, setUser, reloadSettings, signOut }}>{children}</Ctx.Provider>;
}

export function useSession(): Session {
  const s = useContext(Ctx);
  if (!s) throw new Error('useSession outside SessionProvider');
  return s;
}

/** Fetch a GET endpoint; re-runs when `path` changes. `path` null = do not fetch. */
export function useApi<T>(path: string | null) {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(!!path);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    if (!path) return;
    const ctrl = new AbortController();
    setLoading(true);
    api<T>(path, { signal: ctrl.signal })
      .then((d) => {
        setData(d);
        setError('');
      })
      .catch((e) => {
        if ((e as Error).name !== 'AbortError') setError(errorText(e));
      })
      .finally(() => {
        if (!ctrl.signal.aborted) setLoading(false);
      });
    return () => ctrl.abort();
  }, [path, tick]);

  const reload = useCallback(() => setTick((t) => t + 1), []);
  return { data, error, loading, reload, setData };
}
